"use client";

import Navbar from "../../components/Layout/Navbar";
import Footer from "../../components/Layout/Footer";
import PageMeta from "../../components/pagesHelmet/PageMeta";

const BRAND = {
  primary: "#3B82F6",
  primaryDark: "#2563EB",
  primarySoft: "rgba(59, 130, 246, 0.1)",
  success: "#22C55E",
  warning: "#F59E0B",
  muted: "#6B7280",
  text: "#1F2937",
};

export default function PrivacyPolicyPage() {
  return (
    <>
    <PageMeta 
  title="Privacy Policy | U2LR Dashboard" 
  description="Read U2LR's Privacy Policy to understand how we collect, use, and protect your personal information on our URL shortener and analytics platform." 
/>
      <Navbar />

      <div className="bg-white">

        {/* ================= HERO ================= */}
        <section
          className="text-white"
          style={{
            background: `linear-gradient(135deg, #000, #4b76d1)`,
          }}
        >
          <div className="container py-5">
            <div className="row justify-content-center text-center">
              <div className="col-lg-8 py-5">
                <h1 className="display-6 fw-bold mb-3">Privacy Policy</h1>
                <p className="opacity-75 mb-0">
                  Your privacy matters to us. This policy explains how u2lr
                  collects, uses, and protects your data.
                </p>
                <small className="d-block mt-3 opacity-75">
                  Last updated: December 19, 2025
                </small>
              </div>
            </div>
          </div>
        </section>

        {/* ================= CONTENT ================= */}
        <section className="py-5">
          <div className="container py-4">
            <div className="row justify-content-center">
              <div className="col-lg-9">

                <PolicySection title="1. Introduction & Scope">
                  <p>
                    u2lr respects your privacy and is committed to protecting
                    your personal data. This Privacy Policy explains how data is
                    collected, used, stored, and protected when you use
                    u2lr.com.
                  </p>
                  <p>
                    This policy applies to our website, URL shortening services,
                    analytics tools, dashboards, APIs, and related services
                    operated by u2lr.
                  </p>
                </PolicySection>

                <PolicySection title="2. Information We Collect">
                  <h6 className="fw-semibold mt-3">2.1 Information You Provide</h6>
                  <ul>
                    <li>Email address</li>
                    <li>Username or account identifier</li>
                    <li>Authentication credentials (hashed or encrypted)</li>
                    <li>Support messages or inquiries</li>
                  </ul>

                  <h6 className="fw-semibold mt-3">2.2 Link & Usage Data</h6>
                  <ul>
                    <li>Original and shortened URLs</li>
                    <li>Link creation dates and settings</li>
                    <li>Click counts and performance metrics</li>
                  </ul>

                  <h6 className="fw-semibold mt-3">2.3 Automatically Collected Data</h6>
                  <ul>
                    <li>IP address (anonymized where possible)</li>
                    <li>Browser, device, and operating system</li>
                    <li>Referrer URLs</li>
                    <li>Approximate location (country/region)</li>
                    <li>Date and time of access</li>
                  </ul>
                </PolicySection>

                <PolicySection title="3. How We Use Collected Data">
                  <ul>
                    <li>Operate and maintain u2lr services</li>
                    <li>Shorten, redirect, and manage URLs</li>
                    <li>Generate analytics and reports</li>
                    <li>Improve security and prevent abuse</li>
                    <li>Enhance platform performance and reliability</li>
                    <li>Respond to support requests</li>
                    <li>Comply with legal obligations</li>
                  </ul>
                  <p className="fw-semibold">
                    We do not sell personal data to third parties.
                  </p>
                </PolicySection>

                <PolicySection title="4. Cookies & Tracking Technologies">
                  <p>
                    u2lr uses cookies for authentication, security, preferences,
                    and analytics. You can control cookie usage through your
                    browser settings, though disabling cookies may affect
                    functionality.
                  </p>
                </PolicySection>

                <PolicySection title="5. Third-Party Services">
                  <p>
                    We may use trusted third-party providers for hosting,
                    analytics, email notifications, and payment processing (if
                    applicable). These providers only receive data necessary to
                    perform their services.
                  </p>
                </PolicySection>

                <PolicySection title="6. Data Retention">
                  <p>
                    We retain data only as long as necessary to provide services,
                    meet legal obligations, and ensure platform security. You
                    may delete your account or links at any time.
                  </p>
                </PolicySection>

                <PolicySection title="7. Data Security">
                  <ul>
                    <li>Secure infrastructure and access controls</li>
                    <li>Encrypted connections (HTTPS/TLS)</li>
                    <li>Restricted internal access</li>
                    <li>Monitoring for abuse or unauthorized access</li>
                  </ul>
                </PolicySection>

                <PolicySection title="8. Your Privacy Rights">
                  <p>
                    Depending on your location, you may have rights to access,
                    correct, delete, or restrict processing of your data.
                  </p>
                  <p>
                    <strong>GDPR:</strong> EU/UK users have rights including data
                    portability and complaint submission.
                    <br />
                    <strong>CCPA/CPRA:</strong> California residents may request
                    data disclosure or deletion.
                  </p>
                </PolicySection>

                <PolicySection title="9. Children’s Privacy">
                  <p>
                    u2lr is not intended for children under 13 (or under 16 where
                    required by law). We do not knowingly collect data from
                    children.
                  </p>
                </PolicySection>

                <PolicySection title="10. International Data Transfers">
                  <p>
                    u2lr operates globally. Data may be processed or stored
                    outside your country, with appropriate safeguards applied
                    where required.
                  </p>
                </PolicySection>

                <PolicySection title="11. Policy Updates">
                  <p>
                    We may update this policy periodically. Continued use of
                    u2lr after updates constitutes acceptance of the revised
                    policy.
                  </p>
                </PolicySection>

                <PolicySection title="12. Contact Information">
                  <p className="mb-1"><strong>Email:</strong> support@u2lr.com</p>
                  <p className="mb-1"><strong>Company:</strong> u2lr</p>
                  <p className="mb-0"><strong>Website:</strong> https://u2lr.com</p>
                </PolicySection>

              </div>
            </div>
          </div>
        </section>

        {/* ================= FOOT NOTE ================= */}
        <section
          className="text-center py-5"
          style={{ backgroundColor: BRAND.primarySoft }}
        >
          <div className="container">
            <p className="mb-0 text-muted">
              u2lr is built with privacy, transparency, and user trust in mind.
            </p>
          </div>
        </section>

      </div>

      <Footer />
    </>
  );
}

/* ================= HELPER ================= */

function PolicySection({ title, children }) {
  return (
    <div className="mb-5">
      <h4 className="fw-bold mb-3">{title}</h4>
      <div className="text-muted fs-6">{children}</div>
    </div>
  );
}